
import { useState, useRef, useEffect } from "react";
import { SendIcon, Loader2, BotIcon, UserCircle } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useChatMessages, ChatMessage } from "@/hooks/useChatMessages";
import { ScrollArea } from "@/components/ui/scroll-area";

interface AIChatInterfaceProps {
  promptId: string | null;
}

const AIChatInterface = ({ promptId }: AIChatInterfaceProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [promptContent, setPromptContent] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const { messages, addMessage, clearMessages } = useChatMessages();

  useEffect(() => {
    if (promptId) {
      fetchPromptContent(promptId);
      clearMessages();
    }
  }, [promptId, clearMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchPromptContent = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('prompts')
        .select('content')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      
      setPromptContent(data.content);
      
      // Add system message
      addMessage({
        id: Date.now().toString(),
        role: "assistant",
        content: "I'll help you with this prompt. Type your message to begin.",
        timestamp: new Date().toISOString(),
      });
    } catch (error: any) {
      console.error('Error fetching prompt:', error);
      toast({
        title: "Error",
        description: "Failed to load prompt content",
        variant: "destructive",
      });
    }
  };
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!input.trim() || !promptId || !promptContent) return;
    
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: input,
      timestamp: new Date().toISOString(),
    };
    
    addMessage(userMessage);
    setInput("");
    setIsLoading(true);
    
    try {
      // Call the OpenAI function with the input and prompt template
      const { data, error } = await supabase.functions.invoke("openai-chat", {
        body: {
          userMessage: input,
          promptTemplate: promptContent,
        },
      });
      
      if (error) throw error;
      
      const assistantMessage: ChatMessage = {
        id: Date.now().toString(),
        role: "assistant",
        content: data.message,
        timestamp: new Date().toISOString(),
      };
      
      addMessage(assistantMessage);
    } catch (error: any) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to get a response. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <>
      <ScrollArea className="flex-1 p-4">
        {messages.length === 0 && !promptId && (
          <div className="h-full flex items-center justify-center text-center text-muted-foreground p-8">
            <div>
              <BotIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="text-lg font-medium mb-2">Select a prompt to begin</h3>
              <p>Choose a prompt from the sidebar to start testing it with the AI chat.</p>
            </div>
          </div>
        )}
        
        {messages.map((message) => (
          <div
            key={message.id}
            className={`mb-4 flex ${
              message.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`${
                message.role === "user"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted"
              } rounded-lg px-4 py-2 max-w-[80%]`}
            >
              <div className="flex items-center gap-2 mb-1">
                {message.role === "user" ? (
                  <UserCircle className="h-4 w-4" />
                ) : (
                  <BotIcon className="h-4 w-4" />
                )}
                <span className="text-xs">
                  {message.role === "user" ? "You" : "AI Assistant"}
                </span>
              </div>
              <p className="whitespace-pre-wrap">{message.content}</p>
              <div className="text-xs opacity-70 text-right mt-1">
                {new Date(message.timestamp).toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </ScrollArea>
      
      <div className="border-t p-4">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={promptId ? "Type your message..." : "Select a prompt first"}
            className="min-h-[60px] flex-1"
            disabled={isLoading || !promptId}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
          />
          <Button
            type="submit"
            disabled={isLoading || !input.trim() || !promptId}
            className="self-end"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <SendIcon className="h-4 w-4" />
            )}
          </Button>
        </form>
      </div>
    </>
  );
};

export default AIChatInterface;
